import { StateCreator, StoreMutatorIdentifier } from 'zustand'
import type { Brick } from '@models/brick'

/**
 * History Middleware for Brick Builder 3D
 *
 * Tracks actions performed on the brick store and enables undo/redo functionality.
 *
 * Features:
 * - Records addBrick, removeBrick, and rotateBrick actions
 * - Maintains history stack with max 50 actions
 * - Provides undo/redo methods
 * - Returns descriptive action names for UI feedback
 * - Clears redo stack when new action is performed
 *
 * History is stored OUTSIDE of Immer state to avoid frozen object issues.
 */

/**
 * Action types that can be undone/redone
 */
export type HistoryActionType = 'ADD_BRICK' | 'REMOVE_BRICK' | 'ROTATE_BRICK'

/**
 * History action with all necessary data for undo/redo
 */
export interface HistoryAction {
  type: HistoryActionType
  description: string
  data: {
    brick?: Brick // For ADD_BRICK and REMOVE_BRICK
    brickId?: string // For all actions
    previousRotation?: number // For ROTATE_BRICK
    newRotation?: number // For ROTATE_BRICK
  }
}

/**
 * History state added to the store
 */
export interface HistoryState {
  undo: () => string | null
  redo: () => string | null
  canUndo: () => boolean
  canRedo: () => boolean
  _recordAction: (action: HistoryAction) => void
}

/**
 * Type for the history middleware
 */
export type HistoryMiddleware = <
  T,
  Mps extends [StoreMutatorIdentifier, unknown][] = [],
  Mcs extends [StoreMutatorIdentifier, unknown][] = []
>(
  f: StateCreator<T, Mps, Mcs>,
  maxHistorySize?: number
) => StateCreator<T & HistoryState, Mps, Mcs>

/**
 * History Middleware Implementation
 *
 * Wraps the store and adds undo/redo functionality.
 * History is stored outside of Immer state to avoid frozen object issues.
 *
 * @param f - Store creator function
 * @param maxHistorySize - Maximum number of actions to keep in history (default: 50)
 */
export const historyMiddleware: HistoryMiddleware =
  (f, maxHistorySize = 50) =>
  (set, get, api) => {
    // Store history OUTSIDE of Immer state to avoid frozen object issues
    let history: HistoryAction[] = []
    let historyIndex = -1
    let isUndoRedoing = false // Flag to prevent recording during undo/redo

    // Create the base store
    const store = f(
      set as any,
      get as any,
      api as any
    )

    // Record an action in history
    const recordAction = (action: HistoryAction) => {
      // Don't record actions during undo/redo
      if (isUndoRedoing) {
        return
      }

      // Trim history at current index (clear redo stack)
      history = history.slice(0, historyIndex + 1)

      // Add new action
      history.push(action)

      // Trim history if exceeds max size (keep most recent actions)
      if (history.length > maxHistorySize) {
        history.shift()
      } else {
        historyIndex++
      }
    }

    // Undo the last action
    const undo = (): string | null => {
      if (historyIndex < 0) {
        return null // Nothing to undo
      }

      const action = history[historyIndex]
      if (!action) {
        return null
      }

      // Move history index back BEFORE performing the action
      historyIndex--

      // Set flag to prevent recording during undo
      isUndoRedoing = true

      try {
        // Perform the undo operation by calling the actual store methods
        // This bypasses the recording mechanism via the isUndoRedoing flag
        switch (action.type) {
          case 'ADD_BRICK':
            // Undo add = call removeBrick
            if (action.data.brick) {
              const currentStore = get() as any
              currentStore.removeBrick(action.data.brick!.id)
            }
            break

          case 'REMOVE_BRICK':
            // Undo remove = call addBrick
            if (action.data.brick) {
              const currentStore = get() as any
              currentStore.addBrick(action.data.brick)
            }
            break

          case 'ROTATE_BRICK':
            // Undo rotate = call internal _setRotation method
            if (action.data.brickId && action.data.previousRotation !== undefined) {
              const currentStore = get() as any
              currentStore._setRotation(action.data.brickId!, action.data.previousRotation!)
            }
            break
        }
      } finally {
        // Always reset flag
        isUndoRedoing = false
      }

      return action.description
    }

    // Redo the next action
    const redo = (): string | null => {
      const nextIndex = historyIndex + 1

      if (nextIndex >= history.length) {
        return null // Nothing to redo
      }

      const action = history[nextIndex]
      if (!action) {
        return null
      }

      // Move history index forward BEFORE performing the action
      historyIndex++

      // Set flag to prevent recording during redo
      isUndoRedoing = true

      try {
        // Perform the redo operation by calling the actual store methods
        // This bypasses the recording mechanism via the isUndoRedoing flag
        switch (action.type) {
          case 'ADD_BRICK':
            // Redo add = call addBrick
            if (action.data.brick) {
              const currentStore = get() as any
              currentStore.addBrick(action.data.brick)
            }
            break

          case 'REMOVE_BRICK':
            // Redo remove = call removeBrick
            if (action.data.brick) {
              const currentStore = get() as any
              currentStore.removeBrick(action.data.brick!.id)
            }
            break

          case 'ROTATE_BRICK':
            // Redo rotate = call internal _setRotation method
            if (action.data.brickId && action.data.newRotation !== undefined) {
              const currentStore = get() as any
              currentStore._setRotation(action.data.brickId!, action.data.newRotation!)
            }
            break
        }
      } finally {
        // Always reset flag
        isUndoRedoing = false
      }

      return action.description
    }

    // Check if undo is available
    const canUndo = (): boolean => {
      return historyIndex >= 0
    }

    // Check if redo is available
    const canRedo = (): boolean => {
      return historyIndex < history.length - 1
    }

    // Return store with history functionality
    return {
      ...store,
      undo,
      redo,
      canUndo,
      canRedo,
      _recordAction: recordAction,
    } as any
  }

/**
 * Helper to get user-friendly action description
 */
export function getActionDescription(action: HistoryAction): string {
  switch (action.type) {
    case 'ADD_BRICK':
      return `Brick placement (${action.data.brick?.type || 'unknown'})`
    case 'REMOVE_BRICK':
      return `Brick deletion (${action.data.brick?.type || 'unknown'})`
    case 'ROTATE_BRICK':
      return `Brick rotation (${action.data.previousRotation}° → ${action.data.newRotation}°)`
    default:
      return 'Unknown action'
  }
}
